import { useCurrentUser } from '../../contexts/CurrentUser';
import { ampli } from '../../ampli';
import { format } from 'date-fns';
import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { API, ShareAPI } from "../../API";
import { useTranslation } from "react-i18next";
import styles from './GeoChart.module.scss';
import { LoaderSmall } from "../LoaderSmall/LoaderSmall";
import { AddToNarrativeModal } from '../AddToNarrativeModal/AddToNarrativeModal';
import { AddToSourceGroupModal } from '../AddToSourceGroupModal/AddToSourceGroupModal';
import { WidgetContentModal } from '../WidgetContentModal/WidgetContentModal';
import { showRequestError } from '../../utils/requestError';
import { formatWithCustomGigaBillion } from '../../utils/fomatNumbers';
import { getSentiment } from '../../utils/sentiment';
import { ECHARTS_TOOLTIP_STYLE, escapeHtml } from '../CustomDashboard/WidgetRenderer/echartsUtils';
import { getWorldGeoJson, getCountryName } from '../CustomDashboard/WidgetRenderer/worldMapGeoJson';
import {
  MAP_SENTIMENT_COLORS,
  SENTIMENT_ORDER,
  normalizeSentiment,
  formatSentimentLabel,
} from '../CustomDashboard/WidgetRenderer/sentimentColors';
import { ReactComponent as ZoomInIcon } from '../../assets/zoomIn.svg';
import { ReactComponent as ZoomOutIcon } from '../../assets/zoomOut.svg';
import { ReactComponent as ResetViewIcon } from '../../assets/resetView.svg';

// Countries are filled with the same sentiment palette as the custom-dashboard
// map widget; publication counts live in the tooltip.
const LAND_COLOR = '#e9effd';
const BORDER_COLOR = '#ffffff';
const CHART_HEIGHT = 500;
const MIN_ZOOM = 1;
const MAX_ZOOM = 8;
const ZOOM_STEP = 1.4;

// The breakdown endpoint returns only an average score (POSITIVE=+1, NEUTRAL=0,
// NEGATIVE=-1), bucketed with the shared ±0.3 thresholds. A categorical
// `sentiment` field (e.g. 'controversial') wins when the backend sends one.
const getCountrySentiment = (row) => {
  const explicit = normalizeSentiment(row?.sentiment ?? row?.opinion_balance);
  if (explicit) return explicit;
  const score = row?.average_sentiment_score;
  if (score === null || score === undefined) return null;
  return normalizeSentiment(getSentiment(score));
};

// Google GeoChart drew a Mercator world; keep the same perspective so country
// sizes and marker positions look familiar (echarts defaults to equirectangular).
// Hand-rolled on purpose: d3's geoMercator wraps longitudes back into
// [-180, 180], which undoes the >180° shift worldMapGeoJson applies to rings
// crossing the antimeridian (Chukotka) and draws streaks across the map.
const MAX_MERCATOR_LAT = 85;
const toRad = Math.PI / 180;
const MERCATOR_PROJECTION = {
  // Screen orientation (y grows southward), as echarts expects from a projection.
  project: ([lng, lat]) => {
    const clampedLat = Math.max(-MAX_MERCATOR_LAT, Math.min(MAX_MERCATOR_LAT, lat));
    return [lng * toRad, -Math.log(Math.tan(Math.PI / 4 + (clampedLat * toRad) / 2))];
  },
  unproject: ([x, y]) => [x / toRad, (2 * Math.atan(Math.exp(-y)) - Math.PI / 2) / toRad],
};

let worldMapRegistered = false;
const ensureWorldMapRegistered = () => {
  if (worldMapRegistered) return;
  echarts.registerMap('world', getWorldGeoJson());
  worldMapRegistered = true;
};

// ISO2 codes of the world GeoJSON features, plus a lookup by lower-cased
// English country name, so API rows can be matched either way.
let codeLookup = null;
const getCodeLookup = () => {
  if (codeLookup) return codeLookup;
  const codes = new Set();
  const byName = {};
  (getWorldGeoJson().features ?? []).forEach((feature) => {
    const code = feature?.properties?.name;
    if (!code) return;
    codes.add(code);
    byName[getCountryName(code).toLowerCase()] = code;
  });
  codeLookup = { codes, byName };
  return codeLookup;
};

export const GeoChart = ({
  report,
  story,
  sourceGroups,
  countries,
  countryGroups = [],
  startDate,
  endDate,
  isShare,
}) => {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const [modal, setModal] = useState({isVisible: false, country: null, title: ''});
  const [countriesData, setCountriesData] = useState(null);
  const [allCountries, setAllCountries] = useState(null);
  const [addToCaseModal, setAddToCaseModal] = useState({ isOpen: false, countries: null, sourceId: null });
  const [addToGroupModal, setAddToGroupModal] = useState({ isOpen: false, countries: null, sourceId: null });

  const api = isShare ? ShareAPI : API;

  const fetchAllCountries = () => {
    api.fetch('GET', '/API/v1/countries').then(data => {
      setAllCountries(data);
    }).catch((e) => { showRequestError(e, t('Geographic distribution')); setAllCountries([]); });
  };

  useEffect(() => {
    fetchAllCountries();
  }, []);

  const fetchCountriesData = () => {
    const urlParams = new URLSearchParams();
    if(sourceGroups.length > 0) {
      sourceGroups.forEach((sourceGroup) => {
        urlParams.append('source_group_ids', sourceGroup.value);
      });
    }
    if(countries.length > 0) {
      countries.forEach((country) => {
        urlParams.append('source_country_ids', country.value);
      });
    }
    if (countryGroups.length > 0) {
      countryGroups.forEach((groupId) => {
        urlParams.append('country_group_ids', groupId);
      });
    }
    if(startDate) {
      urlParams.append('date_from',format(startDate, 'yyyy-LL-dd'));
    }
    if(endDate) {
      urlParams.append('date_to',format(endDate, 'yyyy-LL-dd'));
    }

    api.fetch('GET', `/API/v1/topic-modeling/${report.id}/events/${story.id}/countries/breakdown?${urlParams.toString()}`).then(data => {
      setCountriesData(data);
    }).catch((e) => { showRequestError(e, t('Geographic distribution')); setCountriesData([]); })
  }

  useEffect(() => {
    fetchCountriesData();
  }, [])

  const getCountryOption = (countryName) => {
    if (!allCountries) return null;
    let country = allCountries?.objects?.find(country => country.name === countryName);
    return country ? {value: country.id, label: country.name} : null;
  }

  // AddToNarrativeModal / AddToSourceGroupModal send `countries` verbatim as
  // `source_origin_country_ids`, so they need plain ids, not select options.
  const getCountryIds = (countryName) => {
    const option = getCountryOption(countryName);
    return option ? [option.value] : null;
  };

  // One entry per API row that matches a GeoJSON feature, keyed by ISO2 code:
  // { code, name, count, sentiment }.
  const countriesByCode = useMemo(() => {
    const rows = countriesData?.objects ?? [];
    if (rows.length === 0) return {};
    const { codes, byName } = getCodeLookup();
    const codeByName = {};
    (allCountries?.objects ?? []).forEach((country) => {
      if (country?.name && country?.code) codeByName[country.name] = country.code;
    });

    const result = {};
    rows.forEach((country) => {
      const code = [country.code, codeByName[country.name]].find((candidate) => candidate && codes.has(candidate))
        || byName[String(country.name ?? '').toLowerCase()];
      if (!code) return;
      result[code] = {
        code,
        name: country.name,
        count: country.messages_count ?? 0,
        sentiment: getCountrySentiment(country),
      };
    });
    return result;
  }, [countriesData, allCountries]);

  // Sentiments present in the data, in canonical order, for the legend.
  const legendSentiments = useMemo(() => {
    const present = new Set(Object.values(countriesByCode).map((c) => c.sentiment).filter(Boolean));
    return SENTIMENT_ORDER.filter((s) => present.has(s));
  }, [countriesByCode]);

  const option = useMemo(() => {
    ensureWorldMapRegistered();
    const entries = Object.values(countriesByCode);

    // Country fills by sentiment with a darker hover, like ChartMap.
    const regions = entries
      .filter((c) => c.sentiment)
      .map((c) => ({
        name: c.code,
        itemStyle: { areaColor: MAP_SENTIMENT_COLORS[c.sentiment].fill },
        emphasis: { itemStyle: { areaColor: MAP_SENTIMENT_COLORS[c.sentiment].hover } },
      }));

    return {
      aria: { enabled: true },
      tooltip: {
        trigger: 'item',
        appendToBody: true,
        ...ECHARTS_TOOLTIP_STYLE,
        formatter: (params) => {
          const code = params.name;
          const row = countriesByCode[code];
          const title = row?.name ?? getCountryName(code);
          if (!row) return `<div style="font-weight:600">${escapeHtml(title)}</div>`;
          const sentimentLabel = row.sentiment ? t(formatSentimentLabel(row.sentiment)) : '—';
          return `<div style="font-weight:600;margin-bottom:4px">${escapeHtml(title)}</div>`
            + `<div>${escapeHtml(t('Publications'))}: <b>${escapeHtml(formatWithCustomGigaBillion(row.count))}</b></div>`
            + `<div>${escapeHtml(t('Sentiment'))}: <b>${escapeHtml(sentimentLabel)}</b></div>`;
        },
      },
      geo: {
        map: 'world',
        projection: MERCATOR_PROJECTION,
        // Drag to pan only; zoom goes through the buttons (wheel is blocked).
        roam: 'move',
        zoom: MIN_ZOOM,
        scaleLimit: { min: MIN_ZOOM, max: MAX_ZOOM },
        // Only vertical insets: setting all four edges makes echarts stretch
        // the map to the box and lose the aspect ratio. Width stays auto, so
        // the map fills the card height and is centered horizontally.
        top: 8,
        bottom: 8,
        // Same visible extent as the old map: Greenland to the tip of South
        // America (Mercator explodes beyond ~80° so the poles are clipped).
        boundingCoords: [[-180, 80], [180, -57]],
        itemStyle: { areaColor: LAND_COLOR, borderColor: BORDER_COLOR, borderWidth: 1 },
        // Countries without data don't react to hover; the ones with data get
        // their darker sentiment hover from `regions`.
        emphasis: { label: { show: false }, itemStyle: { areaColor: LAND_COLOR } },
        select: { disabled: true },
        regions,
      },
      series: [
        // Hit area for countries with data (tooltip + click on the whole
        // region). Colors come from geo.regions.
        {
          type: 'map',
          map: 'world',
          geoIndex: 0,
          name: t('Publications'),
          data: entries.map((c) => ({ name: c.code, value: c.count })),
          nameProperty: 'name',
          selectedMode: false,
        },
      ],
    };
  }, [countriesByCode, t]);

  const chartRef = useRef(null);

  // Zoom around the viewport center; scaleLimit clamps the result.
  const zoomBy = useCallback((factor) => {
    const chart = chartRef.current?.getEchartsInstance();
    if (!chart) return;
    chart.dispatchAction({
      type: 'geoRoam',
      componentType: 'geo',
      geoIndex: 0,
      zoom: factor,
      originX: chart.getWidth() / 2,
      originY: chart.getHeight() / 2,
    });
  }, []);

  // notMerge rebuilds the geo component from the option, which drops the
  // roamed center/zoom and recenters the map.
  const resetView = useCallback(() => {
    chartRef.current?.getEchartsInstance()?.setOption(option, true);
  }, [option]);

  const onEvents = useMemo(() => ({
    click: (params) => {
      if (params?.componentType !== 'series') return;
      const country = countriesByCode[params.name]?.name;
      if (!country) return;
      setModal({
        isVisible: true,
        country: country,
        title: country,
      });
      ampli.track({
        event_type: 'Open wiget popup in event',
        event_properties: {
          user_id: currentUser?.id,
          workspace_id: currentUser?.workspace_id,
          report_id: report?.id,
          popup_type: 'Geochart',
          popup_value: 'Country: ' + country,
        }
      });
    },
  }), [countriesByCode, currentUser?.id, currentUser?.workspace_id, report?.id]);

  if (!countriesData) {
    return <div className={styles.widgetLoader}> <LoaderSmall /> </div>
  }

  return (
    <div className={styles.chartWrapper} translate='no'>
      <ReactECharts
        ref={chartRef}
        option={option}
        notMerge
        style={{ width: '100%', height: `${CHART_HEIGHT}px` }}
        opts={{ renderer: 'svg' }}
        onEvents={onEvents}
      />

      <div className={styles.mapControls} role="toolbar" aria-label={t('Map controls')} aria-orientation="vertical">
        <button
          type="button"
          className={styles.mapControlsBtn}
          onClick={() => zoomBy(ZOOM_STEP)}
          aria-label={t('Zoom in')}
          title={t('Zoom in')}
        >
          <ZoomInIcon aria-hidden="true" />
        </button>
        <button
          type="button"
          className={styles.mapControlsBtn}
          onClick={() => zoomBy(1 / ZOOM_STEP)}
          aria-label={t('Zoom out')}
          title={t('Zoom out')}
        >
          <ZoomOutIcon aria-hidden="true" />
        </button>
        <span className={styles.mapControlsDivider} aria-hidden="true" />
        <button
          type="button"
          className={styles.mapControlsBtn}
          onClick={resetView}
          aria-label={t('Reset view')}
          title={t('Reset view')}
        >
          <ResetViewIcon aria-hidden="true" />
        </button>
      </div>

      {legendSentiments.length > 0 ? (
        <ul className={styles.sentimentLegend} aria-label={t('Sentiment legend')}>
          {legendSentiments.map((sentiment) => (
            <li key={sentiment} className={styles.sentimentLegendItem}>
              <span
                aria-hidden="true"
                className={styles.sentimentLegendSwatch}
                style={{ background: MAP_SENTIMENT_COLORS[sentiment].fill }}
              />
              {t(formatSentimentLabel(sentiment))}
            </li>
          ))}
        </ul>
      ) : (
        ''
      )}

      {!countriesData.objects?.length ? (
        <div className={styles.noDataContainer}>
          {t('There is no data to display')}
        </div>
      ) : (
        ''
      )}

      {modal.isVisible ? (
        <WidgetContentModal
          infoModal={modal.isVisible}
          tabIndex={1}
          isChart={true}
          title={modal.title || ''}
          platformOption={null}
          type={null}
          subType={null}
          stateAffiliatedId={null}
          countryId={modal.country ? [getCountryOption(modal.country)] : null}
          // platformsOptions={platformOptions}
          audienceRange={null}
          narrative={report}
          sourceGroup={
            sourceGroups.length > 0
              ? sourceGroups?.map((sourceGroup) => sourceGroup.value)
              : null
          }
          source={null}
          sentimentOptions={null}
          eventIdsOptions={[story.id]}
          startDate={startDate}
          endDate={endDate}
          contentTypeOptions={null}
          isShare={isShare}
          onChange={(bool) => {
            setModal({ isVisible: bool, country: null, title: '' });
          }}
          showAddToActions={true}
          onAddToCase={(bool, sourceId) => {
            setAddToCaseModal({
              isOpen: bool,
              countries: getCountryIds(modal.country),
              sourceId: sourceId || null,
            });
          }}
          onAddToGroup={(bool, sourceId) => {
            setAddToGroupModal({
              isOpen: bool,
              countries: getCountryIds(modal.country),
              sourceId: sourceId || null,
            });
          }}
        />
      ) : (
        ''
      )}

      {addToCaseModal.isOpen ? (
        <AddToNarrativeModal
          isOpen={addToCaseModal.isOpen}
          deduplication={false}
          caseId={report?.id}
          eventIds={[story?.id]}
          areAllMessagesSelected={true}
          startDate={startDate}
          endDate={endDate}
          sourceGroups={sourceGroups}
          sources={addToCaseModal.sourceId ? [addToCaseModal.sourceId] : []}
          countries={addToCaseModal.countries}
          onChange={(bool) => {
            setAddToCaseModal({
              isOpen: bool,
              contentTypes: null,
              sentiment: null,
              sourceId: null,
            });
          }}
        />
      ) : (
        ''
      )}
      {addToGroupModal.isOpen ? (
        <AddToSourceGroupModal
          isOpen={addToGroupModal.isOpen}
          deduplication={false}
          areAllMessagesSelected={true}
          startDate={startDate}
          endDate={endDate}
          sourceGroups={sourceGroups}
          sourcesFilter={addToGroupModal.sourceId ? [addToGroupModal.sourceId] : []}
          countries={addToGroupModal.countries}
          narrative={report}
          eventIds={[story?.id]}
          onChange={(bool) => {
            setAddToGroupModal({
              isOpen: bool,
              contentTypes: null,
              sentiment: null,
              sourceId: null,
            });
          }}
        />
      ) : (
        ''
      )}
    </div>
  );
};